import { Check } from 'lucide-react'
import * as React from 'react'

import { filterComboboxItems } from 'components/brokers/needs_attention/utils'

import { cn } from '../../../utils'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover'
import LoadingSpinnerCentered from '../LoadingSpinnerCentered'

export interface ComboboxFilterOption<T> {
  label?: string
  value: T
  disabled?: boolean
}

export interface ComboboxFilterGroup<T> {
  groupLabel: string
  options: ComboboxFilterOption<T>[]
}

interface ComboboxFilterProps<T> {
  options: ComboboxFilterGroup<T>[]
  placeholder: string
  selectedValues: T[]
  onSelectValue: (value: T) => void
  excludedValues?: T[]
  onExcludeValue?: (value: T) => void
  width?: string
  children: React.ReactElement
  onValueChange?: (value: string) => void
  isLoading?: boolean
  isOpen?: boolean
}

const ComboboxMultiSelectFilter = <T,>({
  options,
  placeholder,
  selectedValues = [],
  onSelectValue,
  onValueChange = () => {},
  width = '300px',
  children,
  isLoading = false,
  isOpen = false,
}: ComboboxFilterProps<T>) => {
  const [open, setOpen] = React.useState(isOpen)

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    // Reset filtering when closing the popover.
    onValueChange('')
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger>
        {React.cloneElement(children, { open, setOpen })}
      </PopoverTrigger>
      <PopoverContent className='p-0' style={{ width }}>
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={placeholder}
            className='h-9 focus:ring-0 focus:border-transparent border-none outline-none'
            onValueChange={onValueChange}
          />
          <CommandList>
            {isLoading
              ? (
                <div className='py-4'>
                  <LoadingSpinnerCentered />
                </div>
                )
              : (
                <>
                  <CommandEmpty>No option found.</CommandEmpty>
                  {options.map(({ groupLabel, options: groupOptions }) => {
                    const filteredOptions = filterComboboxItems(groupOptions, selectedValues)

                    return (
                      <CommandGroup key={groupLabel} heading={groupLabel}>
                        {filteredOptions.map((option) => (
                          <ComboboxMultiSelectCommandItem
                            key={option.value as string}
                            option={option}
                            onSelectValue={onSelectValue}
                            isSelected={selectedValues.includes(option.value)}
                          />
                        ))}
                      </CommandGroup>
                    )
                  })}
                </>
                )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

type ComboboxMultiSelectCommandItemProps<T> = {
  option: ComboboxFilterOption<T>
  onSelectValue: (value: T) => void
  isSelected: boolean
}

const ComboboxMultiSelectCommandItem = <T,>({
  option,
  onSelectValue,
  isSelected,
}: ComboboxMultiSelectCommandItemProps<T>) => {
  return (
    <CommandItem
      key={option.value as string}
      value={option.value as string}
      onSelect={(currentValue) => {
        onSelectValue(currentValue as T)
      }}
      disabled={option.disabled}
    >
      <div className='flex items-center justify-between w-full'>
        <div>{option.label}</div>
        <div className='flex items-center justify-end space-x-1'>
          <Check
            className={cn(
              isSelected ? 'opacity-100' : 'opacity-0'
            )}
          />
        </div>
      </div>
    </CommandItem>
  )
}

export default ComboboxMultiSelectFilter
